import { type CatalogItemDto, type TaskInteractionType } from '@performance-crm/shared';
import { CalendarClock } from 'lucide-react';
import { AppointmentFields } from './AppointmentFields';
import { AvailabilityHint } from './AvailabilityHint';
import { describeNextStep, effectiveAppointmentAdvisor, type NextStepForm } from './nextStep';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toLocalInput, TYPE_LABEL } from '@/lib/format';

const TASK_TYPES: TaskInteractionType[] = [
  'LLAMADA',
  'WHATSAPP',
  'SE_CITO',
  'VISITA',
  'MAIL',
  'REUNION',
];

/** Sección "¿Cuál es el siguiente paso?": tarea programada o desistimiento con motivo. */
export function NextStepFields({
  value,
  onChange,
  desistReasons,
  idPrefix = 'next',
  projectId,
  availability,
  tz,
  allowDesist = true,
}: {
  value: NextStepForm;
  onChange: (v: NextStepForm) => void;
  desistReasons: CatalogItemDto[];
  /**
   * false = no se ofrece «Desistir lead»: con una separación o venta viva la API lo rechaza (primero se devuelve o
   * anula el proceso), así que al mover a Separación solo cabe programar la siguiente tarea (Lote 5 · H1).
   */
  allowDesist?: boolean;
  /** Con el lead a mano, avisa si la cita choca con la sala, el horario de quien la atiende u otra cita. */
  availability?: { companyId: string; projectId?: string | null; advisorId?: string | null };
  idPrefix?: string;
  /** Proyecto del lead: sugiere la sala de ventas como lugar de la cita. */
  projectId?: string;
  /** Zona horaria de la empresa (`useCompanyTimezone`) para el mínimo del campo de fecha; sin ella, Lima. */
  tz?: string;
}) {
  const set = (patch: Partial<NextStepForm>) => onChange({ ...value, ...patch });
  // La siguiente tarea es de ahora en adelante: una fecha pasada dejaría el lead vencido al instante (la API también
  // la rechaza, desde las 00:00 de hoy en la zona de la empresa).
  const minWhen = toLocalInput(new Date(), tz);
  const kind = allowDesist ? value.kind : 'TASK';
  return (
    <fieldset className="border-primary/30 bg-primary/5 space-y-3 rounded-md border p-3">
      <legend className="text-primary px-1 text-sm font-semibold">
        Siguiente paso (obligatorio)
      </legend>
      {allowDesist && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => set({ kind: 'TASK' })}
            className={`flex-1 rounded-md border px-3 py-1.5 text-sm ${kind === 'TASK' ? 'border-primary bg-primary text-primary-foreground' : 'bg-card'}`}
          >
            Programar tarea
          </button>
          <button
            type="button"
            onClick={() => set({ kind: 'DESIST' })}
            className={`flex-1 rounded-md border px-3 py-1.5 text-sm ${kind === 'DESIST' ? 'border-destructive bg-destructive text-destructive-foreground' : 'bg-card'}`}
            data-testid={`${idPrefix}-desist`}
          >
            Desistir lead
          </button>
        </div>
      )}
      {kind === 'TASK' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-type`}>Tipo</Label>
            <Select
              id={`${idPrefix}-type`}
              value={value.type}
              onChange={(e) => set({ type: e.target.value as TaskInteractionType })}
            >
              {TASK_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABEL[t]}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-when`}>Fecha y hora</Label>
            <Input
              id={`${idPrefix}-when`}
              type="datetime-local"
              value={value.scheduledAt}
              min={minWhen}
              onChange={(e) => set({ scheduledAt: e.target.value })}
              required
            />
            {value.type === 'SE_CITO' && availability && (
              <AvailabilityHint
                companyId={availability.companyId}
                projectId={availability.projectId}
                advisorId={effectiveAppointmentAdvisor(value.appointment, availability.advisorId)}
                advisorLabel={value.appointment.roomAdvisorId ? 'el asesor de sala' : 'el asesor'}
                at={value.scheduledAt}
              />
            )}
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor={`${idPrefix}-desc`}>Detalle (opcional)</Label>
            <Input
              id={`${idPrefix}-desc`}
              value={value.description}
              onChange={(e) => set({ description: e.target.value })}
              placeholder="Ej. Enviar brochure y confirmar cita"
            />
          </div>
          {value.type === 'SE_CITO' && (
            <div className="sm:col-span-2">
              <AppointmentFields
                value={value.appointment}
                onChange={(appointment) => set({ appointment })}
                projectId={projectId}
                companyId={availability?.companyId}
                idPrefix={`${idPrefix}-appt`}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="grid gap-3">
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-reason`}>Motivo de desistimiento</Label>
            <Select
              id={`${idPrefix}-reason`}
              value={value.reason}
              onChange={(e) => set({ reason: e.target.value })}
              required
            >
              <option value="">Selecciona un motivo…</option>
              {desistReasons.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-comment`}>Comentario</Label>
            <Textarea
              id={`${idPrefix}-comment`}
              value={value.comment}
              onChange={(e) => set({ comment: e.target.value })}
              rows={2}
            />
          </div>
        </div>
      )}
    </fieldset>
  );
}

/**
 * Resumen destacado junto al botón Guardar: qué se programará (o que el lead se desistirá) con lo elegido ahora
 * mismo. Como el siguiente paso viene prellenado, así nadie guarda una llamada «por defecto» sin verla (ux-mi-dia-16).
 * Va como **último hijo** de `DialogFooter`: en móvil (columna invertida) queda encima de los botones y en
 * escritorio, a la izquierda de Guardar (`sm:order-first sm:mr-auto`).
 */
export function NextStepSummary({
  value,
  desistReasons = [],
  tz,
}: {
  value: NextStepForm;
  desistReasons?: CatalogItemDto[];
  /** Zona horaria de la empresa: «mañana 10:00 a. m.» se lee en la misma hora en que se eligió. */
  tz?: string;
}) {
  const text = describeNextStep(value, desistReasons, new Date(), tz);
  const desist = value.kind === 'DESIST';
  return (
    <p
      className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium sm:order-first sm:mr-auto ${
        desist
          ? 'border-destructive/30 bg-destructive/10 text-destructive'
          : 'border-primary/30 bg-primary/10 text-primary'
      }`}
      aria-live="polite"
      data-testid="next-step-summary"
    >
      <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>{text}</span>
    </p>
  );
}
